import { api } from './api';

// Refonte 2026-05-21 (cf migration 20260521120000_opportunity_stages_refonte) :
// identified → suspect, qualified → awaiting_client_docs, ajout de
// meeting_booked et awaiting_quote_supplier. no_follow_up conservé pour
// rétro-compat mais non exposé dans le Kanban / Drawer filtres UI.
export type OpportunityStage =
  | 'suspect'
  | 'contacted'
  | 'meeting_booked'
  | 'meeting_done'
  | 'awaiting_client_docs'
  | 'awaiting_quote_supplier'
  | 'quote_sent'
  | 'won'
  | 'lost'
  | 'no_follow_up';
export type LossReason = 'price' | 'coverage' | 'competitor' | 'proximity' | 'timing' | 'other';

/**
 * Canal d'acquisition à l'origine de l'opportunité — demande client 2026-05-22.
 * Miroir de l'enum Prisma `OpportunityOrigin`. Libellés FR mappés via
 * `ORIGIN_VISUAL` (cf plus bas).
 */
export type OpportunityOrigin =
  | 'campaign'
  | 'prospecting'
  | 'recommendation'
  | 'saturation'
  | 'inbound_call'
  | 'linkedin'
  | 'trade_show'
  // Ajout 2026-05-28 (additif, canaux d'acquisition LBC).
  | 'google'
  | 'kompass'
  | 'apporteur'
  | 'direct_canvassing'
  | 'national_registry'
  | 'yellow_pages'
  | 'tv_media'
  | 'other';

export interface OpportunityListItem {
  id: string;
  reference: string;
  stage: OpportunityStage;
  premiumHt: string | null;
  premiumTtc: string | null;
  commissionRate: string | null;
  commissionAmount: string | null;
  /** Frais HT one-shot (€), NOT NULL DEFAULT 0,00 — décision 2026-04-30. */
  fees: string;
  /** Honoraires HT one-shot (€), NOT NULL DEFAULT 0,00 — décision 2026-04-30. */
  honoraires: string;
  /** GENERATED = COALESCE(commission, 0) + fees + honoraires. */
  totalRevenue: string | null;
  autoScore: string | null;
  manualScore: string | null;
  effectiveScore: string | null;
  weightedRevenue: string | null;
  /** GENERATED = total_revenue × effective_score / 100. */
  weightedTotalRevenue: string | null;
  lossReason: LossReason | null;
  lossComment: string | null;
  /** Bloc-notes libre du commercial (plain text, cap 5000 chars). */
  details: string | null;
  signedAt: string | null;
  /** Date d'effet (prise d'effet du contrat) - ISO YYYY-MM-DD. */
  effectiveDate: string;
  lastActivityAt: string | null;
  stageChangedAt: string;
  createdAt: string;
  prospect: {
    id: string;
    type: 'company' | 'individual';
    companyName: string | null;
    siret: string | null;
    firstName: string | null;
    lastName: string | null;
    status: string;
    // Refonte 2026-05-23 : origin remonté depuis le prospect parent (le
    // champ a été déplacé depuis Opportunity). Permet au badge Kanban + au
    // filtre Pipeline de continuer à fonctionner via le prospect.
    origin: OpportunityOrigin | null;
  };
  contractType: { id: string; code: string; label: string };
  assignedUser: { id: string; firstName: string; lastName: string; email: string };
  /** Apporteur d'affaires (optionnel). `null` si l'opp n'en a pas.
   *  Les 3 taux exposés permettent au client de calculer la rétrocom à
   *  la volée = commission × rate_com / 100 + fees × rate_fees / 100
   *  + honoraires × rate_hono / 100 (décision 2026-04-30). */
  apporteur: {
    id: string;
    /** Refonte 2026-05-22 : polymorphe person | company. */
    type: 'person' | 'company';
    /** person : non-null. company : null. */
    firstName: string | null;
    lastName: string | null;
    /** company : non-null. person : null. */
    companyName: string | null;
    /** Pour person : employeur. Pour company : généralement null. */
    company: string | null;
    isActive: boolean;
    retrocommissionRate: string;
    retrocommissionRateFees: string;
    retrocommissionRateHonoraires: string;
  } | null;
  // Refonte 2026-05-23 : `origin` déplacé sur le prospect parent. Disponible
  // via `prospect.origin`. Le type `OpportunityOrigin` reste exporté pour
  // le filtre Pipeline et le KPI revenue-by-origin.
  /** Date de signature estimée saisie via la modale au 1er « Envoyé » sur
   *  un devis (refonte 2026-05-22). Format ISO date YYYY-MM-DD. Reset à
   *  null quand l'opp quitte `quote_sent` (transitions ou closeLost). */
  estimatedSigningDate: string | null;
  /** Dernier RDV (event meeting, max scheduledAt) sur les opps en
   *  meeting_booked / meeting_done — décision produit 2026-06-05. `null` si
   *  l'opp n'est pas à ce stade ou n'a aucun RDV. `done` = RDV marqué fait. */
  lastMeeting: { scheduledAt: string; done: boolean } | null;
  _count: { quotes: number; documents: number; events: number };
  checklistProgress: { required: number; fulfilled: number; percent: number };
}

export interface Quote {
  id: string;
  opportunityId: string;
  insuranceCompanyId: string;
  filePath: string;
  fileName: string;
  fileSize: number;
  status: 'pending' | 'selected' | 'rejected';
  /** Refonte 2026-05-22 : marqueur « envoyé au client ». null = brouillon
   *  interne, set = envoyé. UI : bouton « Envoyé » visible si null, bouton
   *  « Signé » visible si set. Garde backend : on ne peut pas signer
   *  (status=selected) sans `sentAt`. */
  sentAt: string | null;
  uploadedBy: string;
  createdAt: string;
  insuranceCompany: { id: string; name: string };
  uploader: { id: string; firstName: string; lastName: string };
}

export interface OpportunityDetail extends OpportunityListItem {
  quotes: Quote[];
}

export interface OpportunityFilters {
  cursor?: string;
  limit?: number;
  stage?: OpportunityStage;
  /** CSV de stages - utilisé pour le drill-down depuis les graphes KPI. */
  stages?: string;
  loss_reason?: LossReason;
  contract_type_id?: string;
  assigned_user_id?: string;
  team_id?: string;
  company_id?: string;
  min_premium?: number;
  max_premium?: number;
  min_score?: number;
  stale_days?: number;
  /** Filtre opps avec `weighted_revenue >= X` - drill-down "CA prévisionnel". */
  min_weighted_revenue?: number;
  /** Filtres frais & honoraires (drawer Pipeline > Financier) — décision Q11 2026-04-30. */
  min_fees?: number;
  max_fees?: number;
  min_honoraires?: number;
  max_honoraires?: number;
  /** Bornes temporelles (ISO 8601) - drill-down depuis KPI direction. */
  created_after?: string;
  created_before?: string;
  signed_after?: string;
  signed_before?: string;
  stage_changed_after?: string;
  stage_changed_before?: string;
  /** Bornes ISO date (YYYY-MM-DD) sur la prise d'effet - drill-down Trésorerie. */
  effective_after?: string;
  effective_before?: string;
  prospect_id?: string;
  /** Apporteur(s). UUID seul ou CSV pour multi-select (modale filtre Pipeline
   *  + drill-down depuis carte/KPI Top apporteurs). */
  apporteur_id?: string;
  /** Origines (canal d'acquisition). CSV de codes enum (`campaign,linkedin`).
   *  La sentinelle `none` matche les opps sans origine (NULL côté DB). */
  origins?: string;
}

export interface ScoreBreakdown {
  stage: number;
  age: number;
  activity: number;
  completion: number;
  /** Score final 0-100 - 100 % automatique (plus de score manuel V1). */
  score: number;
  /** Contexte des sous-scores - alimente les tooltips d'explication. */
  context: {
    eventsInWindow: number;
    activityWindowDays: number;
    activityReferenceMean: number;
    requiredDocs: number;
    providedDocs: number;
    daysSinceActivity: number;
  };
}

export interface ChecklistItem {
  id: string;
  documentName: string;
  isRequired: boolean;
  sortOrder: number;
  fulfilled: boolean;
  documentId: string | null;
  documentFileName: string | null;
}

export interface Checklist {
  items: ChecklistItem[];
  progress: { required: number; fulfilled: number; percent: number };
}

function qs(filters: Record<string, unknown>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v === undefined || v === null || v === '') continue;
    p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

function auth(token: string) {
  return { accessToken: token };
}

/** Totaux financiers agrégés (cf backend `OpportunitySummaryBucket`). */
export interface OpportunitySummaryBucket {
  count: number;
  commission: number;
  feesHonoraires: number;
  retrocom: number;
  net: number;
}

export interface OpportunitiesSummary {
  overall: OpportunitySummaryBucket;
  /** Totaux par étape pipeline (clé = code stage). Étape absente ⇒ undefined. */
  byStage: Record<string, OpportunitySummaryBucket | undefined>;
}

export const opportunitiesApi = {
  list: (token: string, filters: OpportunityFilters = {}) =>
    api.get<{ data: OpportunityListItem[]; nextCursor: string | null }>(
      `/api/v1/opportunities${qs(filters as Record<string, unknown>)}`,
      auth(token),
    ),

  /** Totaux sur l'ENSEMBLE des opps filtrées (pas la page chargée). Réutilise
   *  les mêmes filtres que `list` (cursor/limit ignorés côté backend). */
  summary: (token: string, filters: OpportunityFilters = {}) =>
    api.get<OpportunitiesSummary>(
      `/api/v1/opportunities/summary${qs(filters as Record<string, unknown>)}`,
      auth(token),
    ),

  get: (token: string, id: string) =>
    api.get<OpportunityDetail>(`/api/v1/opportunities/${id}`, auth(token)),

  create: (token: string, body: Record<string, unknown>) =>
    api.post<OpportunityDetail>('/api/v1/opportunities', body, auth(token)),

  update: (token: string, id: string, body: Record<string, unknown>) =>
    api.patch<OpportunityDetail>(`/api/v1/opportunities/${id}`, body, auth(token)),

  changeStage: (token: string, id: string, stage: OpportunityStage) =>
    api.patch<OpportunityDetail>(`/api/v1/opportunities/${id}/stage`, { stage }, auth(token)),

  reassign: (token: string, id: string, assignedUserId: string) =>
    api.patch<OpportunityDetail>(
      `/api/v1/opportunities/${id}/reassign`,
      { assignedUserId },
      auth(token),
    ),

  closeLost: (
    token: string,
    id: string,
    reason: LossReason,
    comment?: string,
    // ISO date (YYYY-MM-DD) si l'utilisateur a coché « Réattaquable l'année
    // prochaine » dans la modale ; `null` ou omis sinon. Refonte 2026-05-21
    // (décision produit Q8) — déclenche la création d'une tâche de relance
    // côté backend, assignée au commercial titulaire de l'opp.
    reattackOn?: string | null,
  ) =>
    // Flat extension : la réponse expose `reattackTaskId` à côté du payload
    // d'opp standard. `null` si aucune relance planifiée. Permet au client
    // de construire un toast actionnable (cf item #6 dette pipeline V2).
    api.post<OpportunityDetail & { reattackTaskId: string | null }>(
      `/api/v1/opportunities/${id}/close-lost`,
      {
        reason,
        ...(comment ? { comment } : {}),
        ...(reattackOn !== undefined ? { reattackOn } : {}),
      },
      auth(token),
    ),

  reopen: (token: string, id: string) =>
    api.post<OpportunityDetail>(`/api/v1/opportunities/${id}/reopen`, undefined, auth(token)),

  delete: (token: string, id: string) =>
    api.delete<void>(`/api/v1/opportunities/${id}`, auth(token)),

  checklist: (token: string, id: string) =>
    api.get<Checklist>(`/api/v1/opportunities/${id}/checklist`, auth(token)),

  scoreDetails: (token: string, id: string) =>
    api.get<ScoreBreakdown>(`/api/v1/opportunities/${id}/score/details`, auth(token)),

  quotes: {
    list: (token: string, oppId: string) =>
      api.get<Quote[]>(`/api/v1/opportunities/${oppId}/quotes`, auth(token)),

    upload: async (
      token: string,
      oppId: string,
      file: File,
      insuranceCompanyId: string,
    ): Promise<Quote> => {
      const form = new FormData();
      form.append('insuranceCompanyId', insuranceCompanyId);
      form.append('file', file);
      const res = await fetch(`/api/v1/opportunities/${oppId}/quotes`, {
        method: 'POST',
        credentials: 'include',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const text = await res.text();
      const data = text ? JSON.parse(text) : null;
      if (!res.ok) {
        const err = (data as { error?: { code: string; message: string } } | null)?.error;
        throw new Error(err?.message ?? 'Upload failed');
      }
      return data as Quote;
    },

    select: (token: string, id: string) =>
      api.patch<Quote>(`/api/v1/quotes/${id}/select`, {}, auth(token)),
    reject: (token: string, id: string) =>
      api.patch<Quote>(`/api/v1/quotes/${id}/reject`, {}, auth(token)),
    /**
     * Clic « Envoyer » sur un devis (refonte 2026-05-22 / 06-18). La modale
     * s'ouvre à CHAQUE envoi et demande `quoteSentDate` (date d'envoi réelle,
     * peut être antérieure à aujourd'hui). `estimatedSigningDate` n'est
     * demandée + envoyée qu'au 1er Envoyé de l'opp (création de la tâche de
     * relance) ; ignorée côté backend aux envois suivants.
     */
    markSent: (
      token: string,
      id: string,
      input: { quoteSentDate?: string; estimatedSigningDate?: string },
    ) =>
      api.post<{ quote: Quote; taskCreated: boolean; signingTaskId: string | null }>(
        `/api/v1/quotes/${id}/mark-sent`,
        input,
        auth(token),
      ),
    delete: (token: string, id: string) => api.delete<void>(`/api/v1/quotes/${id}`, auth(token)),
    downloadUrl: (token: string, id: string) =>
      api.get<{ url: string; expiresInSeconds: number; fileName: string }>(
        `/api/v1/quotes/${id}/download`,
        auth(token),
      ),
  },
};

/**
 * Tokens visuels par stage du pipeline — **source unique de vérité** consommée
 * par tous les composants UI (badge, barre de colonne Kanban, headers).
 *
 * Centralisation introduite 2026-05-21 (rétrocheck refonte pipeline) pour
 * éliminer la duplication entre `STAGE_BAR_COLOR` (PipelinePage) et
 * `STAGE_STYLES` (StatusBadge) — qui dérivaient à chaque retouche de palette.
 *
 * - `label` : libellé FR validé client LBC
 * - `badge` : classes Tailwind pour la pastille `PipelineStageBadge`
 *             (fond pâle + texte saturé, pattern Stripe)
 * - `bar`   : classes Tailwind pour la barre 3 px en tête de colonne Kanban
 *             (couleur saturée, fond)
 */
export const STAGE_VISUAL: Record<OpportunityStage, { label: string; badge: string; bar: string }> =
  {
    suspect: { label: 'Suspect', badge: 'bg-gray-100 text-gray-700', bar: 'bg-gray-300' },
    contacted: { label: 'Prise de contact', badge: 'bg-blue-50 text-blue-700', bar: 'bg-blue-400' },
    meeting_booked: {
      label: 'RDV pris',
      badge: 'bg-indigo-50 text-indigo-700',
      bar: 'bg-indigo-400',
    },
    meeting_done: {
      label: 'RDV effectué',
      badge: 'bg-violet-50 text-violet-700',
      bar: 'bg-violet-400',
    },
    awaiting_client_docs: {
      label: 'Attente éléments clients',
      badge: 'bg-warning-50 text-warning-600',
      bar: 'bg-warning-500',
    },
    awaiting_quote_supplier: {
      label: 'Attente devis Compagnie',
      badge: 'bg-amber-50 text-amber-700',
      bar: 'bg-amber-500',
    },
    quote_sent: { label: 'Devis émis', badge: 'bg-accent-50 text-accent-600', bar: 'bg-accent' },
    won: { label: 'Signée', badge: 'bg-success-50 text-success-600', bar: 'bg-success-600' },
    lost: { label: 'Perdue', badge: 'bg-danger-50 text-danger-600', bar: 'bg-danger-600' },
    no_follow_up: { label: 'Sans suite', badge: 'bg-gray-100 text-gray-500', bar: 'bg-gray-300' },
  };

/**
 * Alias rétro-compatible — `STAGE_LABELS[stage]` reste dispo pour les call-sites
 * existants. Dérivé de `STAGE_VISUAL`, donc toute modif d'un libellé se fait
 * dans le tableau ci-dessus, pas ici.
 */
export const STAGE_LABELS: Record<OpportunityStage, string> = Object.fromEntries(
  Object.entries(STAGE_VISUAL).map(([k, v]) => [k, v.label]),
) as Record<OpportunityStage, string>;

export const LOSS_REASON_LABELS: Record<LossReason, string> = {
  price: 'Prix trop élevé',
  coverage: 'Garanties insuffisantes',
  competitor: 'Concurrent retenu',
  proximity: 'Proximité / relation existante',
  timing: 'Timing',
  other: 'Autre',
};

/**
 * Source-de-vérité des libellés FR + couleurs badge pour le champ Origine
 * (demande client 2026-05-22). Pattern identique à `STAGE_VISUAL` — toute
 * modification (renommage, palette) se fait ICI et propage à tous les
 * call-sites UI (modale create, fiche opp, card Kanban, filtre pipeline,
 * pill "Filtres actifs", card KPI Direction).
 *
 * Ordre de déclaration = ordre d'affichage dans les selects et donut KPI.
 */
export const ORIGIN_VISUAL: Record<OpportunityOrigin, { label: string; badge: string }> = {
  campaign: { label: 'Campagne', badge: 'bg-accent-50 text-accent-600' },
  prospecting: { label: 'Prospection', badge: 'bg-blue-50 text-blue-700' },
  recommendation: { label: 'Recommandation', badge: 'bg-success-50 text-success-600' },
  saturation: { label: 'Saturation', badge: 'bg-violet-50 text-violet-700' },
  inbound_call: { label: 'Appel entrant', badge: 'bg-warning-50 text-warning-600' },
  linkedin: { label: 'LinkedIn', badge: 'bg-indigo-50 text-indigo-700' },
  trade_show: { label: 'Salon', badge: 'bg-amber-50 text-amber-700' },
  // Ajout 2026-05-28 (additif, canaux d'acquisition LBC). « Autres » en dernier.
  google: { label: 'Google', badge: 'bg-red-50 text-red-700' },
  kompass: { label: 'Kompass', badge: 'bg-teal-50 text-teal-700' },
  apporteur: { label: 'Apporteur', badge: 'bg-emerald-50 text-emerald-700' },
  direct_canvassing: { label: 'Démarchage direct', badge: 'bg-sky-50 text-sky-700' },
  national_registry: { label: 'Registre national', badge: 'bg-slate-100 text-slate-700' },
  yellow_pages: { label: 'Pages jaunes', badge: 'bg-yellow-50 text-yellow-700' },
  tv_media: { label: 'TV / Infos', badge: 'bg-fuchsia-50 text-fuchsia-700' },
  other: { label: 'Autres', badge: 'bg-gray-100 text-gray-600' },
};

/** Alias rétro-compatible — dérivé de `ORIGIN_VISUAL` (single source of truth). */
export const ORIGIN_LABELS: Record<OpportunityOrigin, string> = Object.fromEntries(
  Object.entries(ORIGIN_VISUAL).map(([k, v]) => [k, v.label]),
) as Record<OpportunityOrigin, string>;

/** Ordre stable pour les selects, donuts, légendes. */
export const ORIGIN_ORDER: OpportunityOrigin[] = Object.keys(ORIGIN_VISUAL) as OpportunityOrigin[];

// Étapes du pipeline principal (hors terminal states) - pour le Kanban.
// Refonte 2026-05-21 : 8 colonnes (vs 6 avant). Cohérent avec
// `LINEAR_STAGES` côté backend (opportunities.service.ts).
export const PIPELINE_STAGES: OpportunityStage[] = [
  'suspect',
  'contacted',
  'meeting_booked',
  'meeting_done',
  'awaiting_client_docs',
  'awaiting_quote_supplier',
  'quote_sent',
  'won',
];
